<template>
  <!-- Placement settings -->
  <v-row v-if="selectedPlacement" class="mt-2">
    <v-col cols="12">
      <!-- Placement Header: row 1 (Back only) -->
      <v-btn
        variant="text"
        color="secondary"
        size="small"
        density="comfortable"
        prepend-icon="mdi-keyboard-backspace"
        class="justify-start"
        :disabled="isSaving"
        data-testid="back-btn"
        @click="showPlacements"
      >
        <span class="text-body-2 text-medium-emphasis">Back to Placements</span>
      </v-btn>

      <!-- Placement Header: row 2 (Name left, actions right) -->
      <div
        class="d-flex flex-column flex-sm-row justify-sm-space-between flex-wrap w-100 pt-2"
      >
        <h2 class="placement__name d-flex align-center">
          <span
            class="text-h4 text-sm-h4 font-weight-medium"
            data-testid="friendly-name-text"
          >
            {{ selectedPlacement?.friendlyName }}
          </span>
        </h2>

        <!-- Placement Header: row 3 (Actions right) -->
        <div class="d-flex align-center mt-2 mt-sm-0">
          <v-tooltip text="Open this placement in a new tab">
            <!-- Preview Button -->
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                data-testid="preview-btn"
                class="mb-2 mr-2"
                size="small"
                color="success"
                @click="showPreview"
              >
                <v-icon>mdi-eye</v-icon>
                <span class="d-none d-sm-inline">Preview</span>
              </v-btn>
            </template>
          </v-tooltip>

          <!-- More Actions Menu -->
          <v-menu location="bottom end">
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                class="mb-2"
                variant="text"
                size="small"
                color="grey-darken-1"
                min-width="60"
                data-testid="more-actions-menu-trigger"
                aria-label="More actions"
              >
                <v-icon size="small" class="mr-1">mdi-dots-vertical</v-icon>
                <span class="text-caption text-medium-emphasis">Actions</span>
              </v-btn>
            </template>
            <v-list density="compact" class="pa-2">
              <!-- Enabled Toggle -->
              <v-list-item class="mb-1" density="compact">
                <template #prepend>
                  <v-switch
                    v-model="placementEnabled"
                    density="compact"
                    hide-details
                    color="success"
                    :disabled="isSaving"
                    data-testid="placement-enabled-toggle"
                    class="ma-0 mr-3"
                  >
                  </v-switch>
                </template>
                <v-list-item-title
                  class="text-body-2 text-medium-emphasis"
                  data-testid="placement-enabled-label"
                >
                  {{ placementEnabled ? 'Enabled' : 'Disabled' }}
                </v-list-item-title>
              </v-list-item>

              <v-divider class="my-2"></v-divider>

              <!-- Copy Embed Code -->
              <v-list-item
                @click="copyToClipboard"
                class="mb-1"
                density="compact"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="grey-darken-1" class="mr-2"
                    >mdi-content-copy</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  {{ copyStatus ? copyStatus : 'Copy embed code' }}
                </v-list-item-title>
              </v-list-item>

              <v-divider class="my-2"></v-divider>

              <!-- Edit Placement Details -->
              <v-list-item
                @click="openEditDetailsDialog"
                class="mb-1"
                density="compact"
              >
                <template #prepend>
                  <v-icon size="small" color="primary" class="mr-2"
                    >mdi-pencil</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-medium-emphasis">
                  Edit Placement Details
                </v-list-item-title>
              </v-list-item>

              <!-- Delete Placement -->
              <v-list-item
                @click="showDeleteDialog = true"
                class="mb-1"
                density="compact"
                color="error"
              >
                <template v-slot:prepend>
                  <v-icon size="small" color="error" class="mr-2"
                    >mdi-delete</v-icon
                  >
                </template>
                <v-list-item-title class="text-body-2 text-error">
                  Delete Placement
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <v-divider class="my-4" />
    </v-col>

    <v-col cols="12">
      <!-- Placement Status Banner -->
      <div v-if="!placementEnabled">
        <v-alert
          type="warning"
          variant="tonal"
          class="mb-0"
          data-testid="placement-disabled-alert"
        >
          <template #prepend>
            <v-icon>mdi-pause-circle</v-icon>
          </template>
          <div class="d-flex align-center justify-space-between">
            <span class="text-body-1 font-weight-medium">
              This placement is currently disabled and will not display to
              users.
            </span>
            <v-btn
              color="success"
              size="small"
              variant="flat"
              @click="togglePlacementEnabled"
              :disabled="isSaving"
              data-testid="enable-placement-btn"
            >
              <v-icon size="small" class="mr-1">mdi-play</v-icon>
              Enable Placement
            </v-btn>
          </div>
        </v-alert>
      </div>
    </v-col>

    <!-- Coupon Management Section -->
    <v-col cols="12">
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <div class="d-flex align-center ga-2">
            <span>Coupons ({{ selectedCoupons.length }})</span>
            <v-tooltip
              text="Only Active coupons will display in this placement. Scheduled/Paused won’t show until Active; Archived never show."
              location="top"
              data-testid="coupons-help-tooltip"
            >
              <template #activator="{ props }">
                <v-icon
                  v-bind="props"
                  size="18"
                  color="secondary"
                  class="d-none d-sm-inline"
                  data-testid="coupons-help-icon"
                  >mdi-help-circle</v-icon
                >
              </template>
            </v-tooltip>
          </div>
          <v-tooltip
            text="Choose from coupons not already in this placement."
            location="top"
            data-testid="add-coupon-tooltip"
          >
            <template #activator="{ props }">
              <v-btn
                v-bind="props"
                color="primary"
                size="small"
                @click="showAddCouponDialog = true"
                :disabled="isLoadingCoupons"
                data-testid="open-add-coupon-dialog-btn"
              >
                <v-icon>mdi-plus</v-icon>
                Add Coupon
              </v-btn>
            </template>
          </v-tooltip>
        </v-card-title>

        <v-card-text>
          <!-- Loading State -->
          <div v-if="isLoadingCoupons" class="text-center py-4">
            <v-progress-circular
              indeterminate
              color="primary"
            ></v-progress-circular>
            <p class="mt-2">Loading coupons...</p>
          </div>

          <!-- Empty State -->
          <div v-else-if="!selectedCoupons.length" class="text-center py-4">
            <v-icon size="48" color="grey-lighten-1">mdi-ticket-percent</v-icon>
            <p class="mt-2 text-grey">No coupons added to this placement</p>
            <v-btn
              color="primary"
              variant="outlined"
              size="small"
              @click="showAddCouponDialog = true"
              class="mt-2"
            >
              Add Your First Coupon
            </v-btn>
          </div>

          <!-- Coupons Table -->
          <v-table
            v-else
            class="sticky-table"
            data-testid="placement-coupons-table"
          >
            <thead>
              <tr>
                <th>Title</th>
                <th>Status</th>
                <th>Start</th>
                <th>End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="coupon in selectedCoupons"
                :key="coupon.couponId"
                :data-coupon-id="coupon.couponId"
              >
                <td>{{ coupon?.design?.title || 'Untitled Coupon' }}</td>
                <td>
                  <v-chip
                    :color="getStatusColor(coupon.status)"
                    size="small"
                    variant="flat"
                  >
                    {{ getStatusText(coupon.status) }}
                  </v-chip>
                </td>
                <td>{{ formatDate(coupon.startTime) }}</td>
                <td>{{ formatDate(coupon.endTime) }}</td>
                <td>
                  <v-tooltip
                    text="Removes this coupon from this placement. The coupon itself is not deleted."
                  >
                    <template #activator="{ props }">
                      <v-btn
                        v-bind="props"
                        size="small"
                        color="error"
                        variant="text"
                        class="text-capitalize"
                        text="Remove"
                        :disabled="isSaving"
                        :data-testid="`remove-coupon-btn-${coupon.couponId}`"
                        @click="removeCoupon(coupon.couponId)"
                      />
                    </template>
                  </v-tooltip>
                </td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>

  <!-- Edit Placement Details Dialog -->
  <v-dialog
    v-model="showEditDetailsDialog"
    max-width="600px"
    data-testid="edit-placement-details-dialog"
  >
    <v-card>
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-2" color="primary">mdi-pencil</v-icon>
        Edit Placement Details
      </v-card-title>
      <v-card-text>
        <v-form @submit.prevent="savePlacementDetails">
          <v-text-field
            v-model="editPlacementName"
            label="Placement Name"
            placeholder="Enter placement name"
            variant="outlined"
            density="comfortable"
            class="mb-4"
            hide-details
            required
            data-testid="edit-placement-name-input"
          />
          <v-text-field
            v-model="editPlacementUrl"
            label="Placement URL"
            placeholder="https://example.com/your-page"
            variant="outlined"
            density="comfortable"
            class="mb-2"
            hide-details
            required
            data-testid="edit-placement-url-input"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="grey-darken-1"
          variant="text"
          @click="showEditDetailsDialog = false"
          data-testid="edit-placement-cancel-btn"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          :loading="isSaving"
          :disabled="!editPlacementName || !editPlacementUrl"
          @click="savePlacementDetails"
          data-testid="edit-placement-save-btn"
        >
          Save & Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Add Coupon Dialog -->
  <v-dialog
    v-model="showAddCouponDialog"
    max-width="800px"
    data-testid="add-coupon-dialog"
  >
    <v-card>
      <v-card-title>Add Coupon to Placement</v-card-title>
      <v-card-text>
        <div v-if="isLoadingAvailableCoupons" class="text-center py-4">
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
          <p class="mt-2">Loading available coupons...</p>
        </div>

        <div v-else-if="!availableCoupons.length" class="text-center py-4">
          <v-icon size="48" color="grey-lighten-1">mdi-ticket-percent</v-icon>
          <p class="mt-2 text-grey">No available coupons found</p>
          <p class="text-caption text-grey">
            Create some coupons first to add them to this placement
          </p>
        </div>

        <div v-else>
          <v-text-field
            v-model="couponSearch"
            label="Search coupons"
            prepend-inner-icon="mdi-magnify"
            variant="outlined"
            density="compact"
            class="mb-4"
            data-testid="add-coupon-search"
          ></v-text-field>

          <v-list>
            <v-list-item
              v-for="coupon in filteredAvailableCoupons"
              :key="coupon.couponId"
              @click="addCoupon(coupon.couponId)"
              :data-testid="`add-coupon-item-${coupon.couponId}`"
            >
              <template v-slot:prepend>
                <v-icon
                  :color="
                    isCouponAlreadyAdded(coupon.couponId) ? 'grey' : 'primary'
                  "
                >
                  {{
                    isCouponAlreadyAdded(coupon.couponId)
                      ? 'mdi-check-circle'
                      : 'mdi-plus-circle'
                  }}
                </v-icon>
              </template>

              <v-list-item-title>
                {{ coupon?.design?.title || 'Untitled Coupon' }}
              </v-list-item-title>

              <v-list-item-subtitle>
                <v-chip
                  :color="getStatusColor(coupon.status)"
                  size="x-small"
                  variant="flat"
                  class="mr-2"
                >
                  {{ getStatusText(coupon.status) }}
                </v-chip>
                {{ formatDate(coupon.startTime) }} -
                {{ formatDate(coupon.endTime) }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          variant="text"
          @click="showAddCouponDialog = false"
        >
          Close
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Delete Placement Dialog -->
  <v-dialog
    v-model="showDeleteDialog"
    max-width="500px"
    data-testid="delete-placement-dialog"
  >
    <v-card>
      <v-card-title>Delete Placement</v-card-title>
      <v-card-text>
        Are you sure you want to delete the placement "{{
          selectedPlacementId
        }}"? This action cannot be undone.
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="text" @click="showDeleteDialog = false">
          Cancel
        </v-btn>
        <v-btn
          color="error"
          @click="removePlacement"
          data-testid="confirm-delete-placement-btn"
          >Delete Placement</v-btn
        >
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Snackbar -->
  <v-snackbar v-model="showSnackbar" :timeout="2000" :color="snackbarColor">
    {{ snackbarText }}

    <template #actions>
      <v-btn icon="mdi-close" @click="showSnackbar = false"></v-btn>
    </template>
  </v-snackbar>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import type { ISettingsGroup, IPlacementSettings } from '@/stores/types'
import type { Coupon, FullCoupon, TransformedCoupon } from '@/interface/Coupon'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useProductSettingsStore } from '@/stores/productSettings'
import { fetchCoupons } from '@/services/coupons'
import { useCounterStore } from '@/stores/counter'

const props = defineProps<{
  accountName: string
  cdn: string
  placements: ISettingsGroup
  placementId: string | null
}>()

const emit = defineEmits<{
  'update:placementId': [value: string | null]
}>()

const cxUrl = import.meta.env.VITE_CX_URL
const validationUrl = `${cxUrl}?account_id=${props.accountName}&placement_id=${props.placementId}`

const originalPlacement = ref(null)
const placementToDelete = ref<string | null>(null)

const showEditDetailsDialog = ref(false)
const editPlacementName = ref('')
const editPlacementUrl = ref('')

const couponStore = useCounterStore()
const accountSettings = useAccountSettingsStore()
const productSettings = useProductSettingsStore()
const placements = computed(() => props.placements ?? {})
const count = ref(0)
const isSaving = ref(false)

// Coupon management state
const isLoadingCoupons = ref(false)
const isLoadingAvailableCoupons = ref(false)
const availableCoupons = ref<FullCoupon[]>([])
const selectedCoupons = ref<FullCoupon[]>([])
const showAddCouponDialog = ref(false)
const couponSearch = ref('')

// Placement selection
const selectedPlacementId = ref<string | null>(props.placementId ?? null)

// Selected placement settings
const selectedPlacement = computed<IPlacementSettings | null>(() => {
  return selectedPlacementId.value
    ? placements.value[selectedPlacementId.value]
    : null
})

// Filter available coupons based on search
const filteredAvailableCoupons = computed(() => {
  return availableCoupons.value.filter(coupon =>
    (coupon?.design?.title ?? '')
      .toLowerCase()
      .includes(couponSearch.value.toLowerCase()),
  )
})

const showSnackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref('success')
const isEditingName = ref(false)
const nameDraft = ref('')

// Get all coupons which can be added,
// i.e., those not already in the placement
const getAvailableCoupons = (): FullCoupon[] => {
  const placementId = selectedPlacementId.value

  if (!placementId) {
    return []
  }

  return couponStore.coupons.filter(coupon => {
    return !(coupon.placements ?? []).includes(placementId)
  })
}

// Load selected coupons data
const getSelectedCoupons = (): FullCoupon[] => {
  const placementId = selectedPlacementId.value

  if (!placementId) {
    return []
  }

  return couponStore.coupons.filter(coupon => {
    return (coupon.placements ?? []).includes(placementId)
  })
}

// Add coupon to placement
const addCoupon = async (couponId: string) => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  // Reference selected coupon
  const couponData = availableCoupons.value.find(
    coupon => coupon.couponId === couponId,
  )

  if (!couponData) {
    console.error('Coupon not found:', couponId)
    return
  }

  // Prepare payload
  const payload = {
    ...couponData,
    placements: [...(couponData.placements || []), selectedPlacementId.value],
  }

  // Update coupon data with placement id
  await couponStore.updateCoupon(payload)
  await couponStore.fetchAndSetCoupons()

  // Sync state with updated data
  selectedCoupons.value = getSelectedCoupons()
  availableCoupons.value = getAvailableCoupons()

  showSnackbar.value = true
  snackbarText.value = 'Coupon added to placement'
  snackbarColor.value = 'success'
}

// Remove coupon from placement
const removeCoupon = async (couponId: string) => {
  console.log('REMOVE')
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  // Reference selected coupon
  const couponData = selectedCoupons.value.find(
    coupon => coupon.couponId === couponId,
  )

  if (!couponData) {
    console.error('Coupon not found:', couponId)
    return
  }

  // Prepare payload
  const payload = {
    ...couponData,
    placements: (couponData.placements ?? []).filter(
      placementId => placementId !== selectedPlacementId.value,
    ),
  }

  // Update coupon data with placement id
  await couponStore.updateCoupon(payload)
  await couponStore.fetchAndSetCoupons()

  // Sync state with updated data
  selectedCoupons.value = getSelectedCoupons()
  availableCoupons.value = getAvailableCoupons()

  showSnackbar.value = true
  snackbarText.value = 'Coupon removed from placement'
  snackbarColor.value = 'success'
}

// Check if coupon is already added
const isCouponAlreadyAdded = (couponId: string) => {
  return selectedCoupons.value.find(coupon => coupon.couponId === couponId)
}

// Utility functions
const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'success'
    case 'pending':
      return 'warning'
    case 'archived':
      return 'grey'
    case 'deleted':
      return 'error'
    default:
      return 'grey'
  }
}

const getStatusText = (status: string) => {
  return status?.charAt(0).toUpperCase() + status?.slice(1) || 'Unknown'
}

const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A'
  return new Date(dateString).toLocaleDateString()
}

const removePreviewPlacements = async () => {
  let hasPreview = false
  const updatedSettings = { ...placements.value }

  for (const [key] of Object.entries(updatedSettings)) {
    if (key.startsWith('preview__')) {
      // Remove preview placements
      delete updatedSettings[key]
      hasPreview = true
    }
  }

  if (hasPreview) {
    await productSettings.saveSettings(updatedSettings)
  }
}

const copyStatus = ref('')
const copyToClipboard = async () => {
  try {
    const content = `<script src='${props.cdn}/coupon-builder/cx/loader.js'><\/script><div class='coupon-builder-cx' data-account-id='${props.accountName}' data-placement-id='${props.placementId}'></div>`
    await navigator.clipboard.writeText(content)
    copyStatus.value = 'Copied to clipboard!'

    // Reset the icon and status after 2 seconds
    setTimeout(() => {
      copyStatus.value = ''
    }, 2000)
  } catch (err) {
    copyStatus.value = 'Failed to copy text'
    console.error('Failed to copy text:', err)
  }
}

const placementEnabled = computed({
  get: () => {
    if (!selectedPlacementId.value || !selectedPlacement.value) {
      return false
    }
    return selectedPlacement.value.enabled ?? true
  },
  set: async (newValue: boolean) => {
    if (!selectedPlacementId.value || !selectedPlacement.value) {
      return
    }

    // Update the local state immediately
    selectedPlacement.value.enabled = newValue

    // Save only the enabled state to backend (not all tab changes)
    if (
      originalPlacement.value &&
      typeof originalPlacement.value === 'object'
    ) {
      try {
        await productSettings.saveSettings(
          {
            [selectedPlacementId.value]: {
              ...(originalPlacement.value as any),
              enabled: newValue,
            },
          },
          false,
        )

        // Update originalPlacement to reflect the saved enabled state
        ;(originalPlacement.value as any).enabled = newValue

        // Show success feedback
        showSnackbar.value = true
        snackbarText.value = `Placement ${newValue ? 'enabled' : 'disabled'} successfully`
        snackbarColor.value = 'success'
      } catch (error) {
        // Revert the local state on error
        selectedPlacement.value.enabled = !newValue

        // Show error feedback
        showSnackbar.value = true
        snackbarText.value = `Failed to ${newValue ? 'enable' : 'disable'} placement`
        snackbarColor.value = 'error'
      }
    }
  },
})

const togglePlacementEnabled = async () => {
  if (!selectedPlacementId.value) return

  const placementId = selectedPlacementId.value
  const previousEnabled = placementEnabled.value
  const nextEnabled = !previousEnabled

  // Optimistic update
  const previousSettings = { ...placements.value }
  const updatedSettings = { ...placements.value }
  updatedSettings[placementId] = {
    ...updatedSettings[placementId],
    enabled: nextEnabled,
  }
  productSettings.updateSettings(updatedSettings)

  // Persist
  const ok = await productSettings.saveSettings(
    { [placementId]: updatedSettings[placementId] },
    false,
  )

  // Feedback and rollback on failure
  showSnackbar.value = true
  if (ok) {
    snackbarText.value = 'Changes saved'
    snackbarColor.value = 'success'
  } else {
    productSettings.updateSettings(previousSettings)
    snackbarText.value = 'Failed to save changes. Please try again.'
    snackbarColor.value = 'error'
  }
}

const startEditingName = () => {
  if (!selectedPlacement.value) return
  nameDraft.value = selectedPlacement.value.friendlyName ?? ''
  isEditingName.value = true
  nextTick(() => {
    // best-effort focus is handled by autofocus on the input
  })
}

const cancelEditingName = () => {
  isEditingName.value = false
}

const saveFriendlyNameDraft = async () => {
  if (!selectedPlacementId.value || !selectedPlacement.value) return

  const placementId = selectedPlacementId.value
  const originalName = (selectedPlacement.value.friendlyName ?? '').trim()
  const newName = (nameDraft.value ?? '').trim()

  // No-op if nothing changed
  if (newName === originalName) {
    isEditingName.value = false
    return
  }

  const previousSettings = { ...placements.value }
  const updatedSettings = { ...placements.value }
  updatedSettings[placementId] = {
    ...updatedSettings[placementId],
    friendlyName: newName,
  }

  // Optimistic update already applied via v-model; ensure store stays in sync
  productSettings.updateSettings(updatedSettings)

  const ok = await productSettings.saveSettings(
    { [placementId]: updatedSettings[placementId] },
    false,
  )

  showSnackbar.value = true
  if (ok) {
    snackbarText.value = 'Changes saved'
    snackbarColor.value = 'success'
    isEditingName.value = false
  } else {
    productSettings.updateSettings(previousSettings)
    snackbarText.value = 'Failed to save changes. Please try again.'
    snackbarColor.value = 'error'
    isEditingName.value = false
  }
}

// Dialog controls
const showDeleteDialog = ref(false)

// Set selected placement
const setSelectedPlacement = (placementId: string) => {
  selectedPlacementId.value = placementId

  // Create a deep copy of the original placement data
  try {
    originalPlacement.value = JSON.parse(
      JSON.stringify(placements.value[placementId] ?? {}),
    )
  } catch (err) {
    console.error('Error adding new placement', err)
  }
}

// Delete placement
const removePlacement = async () => {
  if (!selectedPlacement.value) {
    return
  }

  const placementId = selectedPlacementId.value

  if (placementId === null) {
    return
  }

  const result = await productSettings.deletePlacement(placementId)

  showSnackbar.value = true

  if (result) {
    snackbarText.value = 'Placement deleted successfully'
    snackbarColor.value = 'success'
    emit('update:placementId', null)
  } else {
    snackbarText.value = 'Failed to delete placement'
    snackbarColor.value = 'error'
    showDeleteDialog.value = false
    return
  }

  // Reset states
  placementToDelete.value = null
  showDeleteDialog.value = false
  selectedPlacementId.value = null
}

// Show placements
const showPlacements = () => {
  selectedPlacementId.value = null
  emit('update:placementId', null)
}

// Revert placement changes
const revertPlacementChanges = async () => {
  if (!selectedPlacementId.value || !originalPlacement.value) return

  const id = selectedPlacementId.value

  // Make a fresh copy of the snapshot
  const original = JSON.parse(JSON.stringify(originalPlacement.value))

  // Build a settings object that resets both keys
  const resetSettings = {
    ...placements.value,
    [id]: original,
    [`preview__${id}`]: original,
  }

  // Update the store
  productSettings.updateSettings(resetSettings)

  // push the preview restore to the server,
  // but avoid stomping the real placement (refreshData=false)
  await productSettings.saveSettings({ [`preview__${id}`]: original }, false)

  // Clear the undo buffer
  originalPlacement.value = null
}

const openEditDetailsDialog = () => {
  if (!selectedPlacement.value) return
  // Seed form from current placement values
  editPlacementName.value = selectedPlacement.value.friendlyName || ''
  editPlacementUrl.value = selectedPlacement.value.url || ''
  showEditDetailsDialog.value = true
}

const savePlacementDetails = async () => {
  if (!selectedPlacement.value || !selectedPlacementId.value) return

  const id = selectedPlacementId.value
  const newName = (editPlacementName.value ?? '').trim()
  const newUrl = (editPlacementUrl.value ?? '').trim()

  // Update local state immediately for snappy UI
  selectedPlacement.value.friendlyName = newName
  selectedPlacement.value.url = newUrl

  // Persist ONLY name + url (mirror behavior from the other component)
  if (originalPlacement.value && typeof originalPlacement.value === 'object') {
    try {
      isSaving.value = true
      await productSettings.saveSettings(
        {
          [id]: {
            ...(originalPlacement.value as any),
            friendlyName: newName,
            url: newUrl,
          },
        },
        /* refreshData */ false,
      )

      // Keep the snapshot aligned so other change detection remains accurate
      ;(originalPlacement.value as any).friendlyName = newName
      ;(originalPlacement.value as any).url = newUrl

      // Close + feedback
      showEditDetailsDialog.value = false
      showSnackbar.value = true
      snackbarText.value = 'Placement details updated successfully'
      snackbarColor.value = 'success'
    } catch (err) {
      // Roll back local UI if desired (optional)
      // selectedPlacement.value.friendlyName = (originalPlacement.value as any).friendlyName
      // selectedPlacement.value.url = (originalPlacement.value as any).url

      showSnackbar.value = true
      snackbarText.value = 'Failed to save placement details'
      snackbarColor.value = 'error'
      console.error(err)
    } finally {
      isSaving.value = false
    }
  } else {
    // If for some reason we don't have a snapshot yet, just close with a warning
    showSnackbar.value = true
    snackbarText.value =
      'Original snapshot missing. Re-open this placement and try again.'
    snackbarColor.value = 'warning'
  }
}

const showPreview = async () => {
  nextTick(() => {
    const url = `${import.meta.env.VITE_CX_URL}?account_id=${accountSettings.accountId}&placement_id=${selectedPlacementId.value}`
    window.open(url, '_blank')
  })
}

// Load coupons when component mounts or placement changes
watch(
  () => selectedPlacementId.value,
  async newId => {
    if (newId) {
      availableCoupons.value = getAvailableCoupons()
      selectedCoupons.value = getSelectedCoupons()
    }
  },
  { immediate: true },
)

watch(
  () => props.placementId,
  newId => {
    if (newId) {
      setSelectedPlacement(newId)
    }
  },
  { immediate: true },
)
</script>
<style scoped>
.sticky-table thead th {
  position: sticky;
  top: 0;
  background: white;
  z-index: 1;
}
</style>
